// perf-report.ts — accumulate in-game frame-cadence stats and emit ONE per-game summary to
// /api/telemetry (kind 'perf'), read by the ops dashboard's Performance panel. It answers the
// field version of the synthetic render-perf work (PR #257/#287): "what fraction of REAL frames on
// REAL devices drop below 30fps, and how does that correlate with device config and army size?"
//
// The signal is the displayed frame cadence (rAF interval), bucketed into a small histogram so the
// dashboard can aggregate the tail across sessions and slice it by GPU / memory / cores / peak gobs.
// Best-effort and render-only: the emit is prod-host gated inside track(), bounded per-load, and
// swallows all errors — nothing here can affect the sim or the frame it measures.

import { track, deviceTag } from './telemetry.ts';

// Frame-interval histogram boundaries (ms), at the fps knees 125/60/45/30/20/10. Buckets:
//   b0 <8   b1 8-16   b2 16-22   b3 22-33   b4 33-50   b5 50-100   b6 >=100
// A frame slower than 33ms is below 30fps ("experience-impacting"); the dashboard sums b4+b5+b6.
const kBounds = [8, 16, 22, 33, 50, 100];
// A gap this long is a tab-switch / GC-pause return, not a render stall — don't pollute the histogram.
const kIgnoreAboveMs = 2000;
// Below this many frames the sample is too short to characterize (a few seconds at any real rate).
const kMinFrames = 180;

let active = false;
let ctx: 'mp' | 'sp' = 'mp';
let startMs = 0;
let frames = 0;
let worstMs = 0;
let peakPumpMs = 0;
let peakBlitMs = 0;
let peakGobs = 0;
const hist = new Array<number>(kBounds.length + 1).fill(0);

/** Begin a fresh per-game accumulation (called on the in-game false->true edge). */
export function perfGameStart(context: 'mp' | 'sp'): void {
  active = true;
  ctx = context;
  startMs = perfNow();
  frames = 0;
  worstMs = 0;
  peakPumpMs = 0;
  peakBlitMs = 0;
  peakGobs = 0;
  hist.fill(0);
}

/** Record one displayed frame's wall-clock interval (the rAF cadence). */
export function perfNoteFrame(dtFrameMs: number): void {
  if (!active || dtFrameMs <= 0 || dtFrameMs > kIgnoreAboveMs) return;
  frames++;
  if (dtFrameMs > worstMs) worstMs = dtFrameMs;
  let b = kBounds.length;
  for (let i = 0; i < kBounds.length; i++) {
    if (dtFrameMs < kBounds[i]) {
      b = i;
      break;
    }
  }
  hist[b]++;
}

/** Record a rendered frame's CPU sub-costs (pump = sim+HUD, blit = present). */
export function perfNotePaint(pumpMs: number, blitMs: number): void {
  if (!active) return;
  if (pumpMs > peakPumpMs) peakPumpMs = pumpMs;
  if (blitMs > peakBlitMs) peakBlitMs = blitMs;
}

/** Update the peak live gob count (army size) — sampled ~1/sec, cheap O(1). */
export function perfNoteGobs(n: number): void {
  if (active && n > peakGobs) peakGobs = n;
}

/** End the game: emit the summary (if long enough) and stop accumulating. */
export function perfGameEnd(): void {
  if (!active) return;
  active = false;
  if (frames < kMinFrames) return;
  track('perf', {
    ctx,
    secs: Math.round((perfNow() - startMs) / 1000),
    frames,
    h: hist.slice(),
    worstMs: Math.round(worstMs),
    pumpMs: Math.round(peakPumpMs * 10) / 10,
    blitMs: Math.round(peakBlitMs * 10) / 10,
    gobs: peakGobs,
    ...deviceTag(),
  });
}

function perfNow(): number {
  return typeof performance !== 'undefined' ? performance.now() : 0;
}
